import React, { useState, useEffect } from 'react';
import './Orders.css';
import { API_ENDPOINTS, getFetchConfig } from '../config/api';
import { FileText, ShoppingCart, Calendar, User, Phone, Envelope } from 'phosphor-react';
import { useNavigate } from 'react-router-dom';

const Orders = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [activeTab, setActiveTab] = useState('orders');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadOrdersAndQuotes();
  }, []);

  const loadOrdersAndQuotes = async () => {
    try {
      setLoading(true);
      
      // Carregar pedidos
      const ordersResponse = await fetch(API_ENDPOINTS.ORDERS, getFetchConfig('GET'));
      const ordersData = await ordersResponse.json();
      
      // Carregar orçamentos
      const quotesResponse = await fetch(API_ENDPOINTS.QUOTES, getFetchConfig('GET'));
      const quotesData = await quotesResponse.json();
      
      if (ordersData.status === 'ok') {
        setOrders(ordersData.orders);
      } else {
        setError(ordersData.error || 'Erro ao carregar pedidos');
      }
      
      if (quotesData.status === 'ok') {
        setQuotes(quotesData.quotes);
      } else {
        console.error('Erro ao carregar orçamentos:', quotesData.error);
      }
    } catch (error) {
      setError('Erro ao carregar dados: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const getQuoteStatusColor = (status) => {
    switch (status) {
      case 'pendente': return '#ffc107';
      case 'analisando': return '#17a2b8';
      case 'aprovado': return '#28a745';
      case 'recusado': return '#dc3545';
      default: return '#6c757d';
    }
  };

  const getQuoteStatusText = (status) => {
    switch (status) {
      case 'pendente': return 'Pendente';
      case 'analisando': return 'Em Análise';
      case 'aprovado': return 'Aprovado';
      case 'recusado': return 'Recusado';
      default: return status;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pendente': return '#ffc107';
      case 'processando': return '#17a2b8';
      case 'enviado': return '#007bff';
      case 'entregue': return '#28a745';
      case 'cancelado': return '#dc3545';
      default: return '#6c757d';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pendente': return 'Pendente';
      case 'processando': return 'Processando';
      case 'enviado': return 'Enviado';
      case 'entregue': return 'Entregue';
      case 'cancelado': return 'Cancelado';
      default: return status;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-PT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="orders-container">
        <h1>Meus Pedidos</h1>
        <div className="loading">Carregando pedidos...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="orders-container">
        <h1>Meus Pedidos</h1>
        <div className="error-message">{error}</div>
      </div>
    );
  }

  return (
    <div className="orders-container">
      <h1>Meus Pedidos e Orçamentos</h1>
      
      <div className="tabs">
        <button 
          className={`tab ${activeTab === 'orders' ? 'active' : ''}`}
          onClick={() => setActiveTab('orders')}
        >
          <ShoppingCart size={20} />
          Pedidos de Compra ({orders.length})
        </button>
        <button 
          className={`tab ${activeTab === 'quotes' ? 'active' : ''}`}
          onClick={() => setActiveTab('quotes')}
        >
          <FileText size={20} />
          Orçamentos ({quotes.length})
        </button>
      </div>

      {activeTab === 'orders' && (
        <div className="orders-section">
          {orders.length === 0 ? (
            <div className="empty-state">
              <ShoppingCart size={64} color="#ccc" />
              <h3>Nenhum pedido encontrado</h3>
              <p>Você ainda não fez nenhum pedido. Explore nossos produtos!</p>
            </div>
          ) : (
            <div className="orders-grid">
              {orders.map(order => (
                <div key={order.id} className="order-card">
                  <div className="order-header">
                    <h3>Pedido #{order.id}</h3>
                    <span 
                      className="status-badge"
                      style={{ backgroundColor: getStatusColor(order.status) }}
                    >
                      {getStatusText(order.status)}
                    </span>
                  </div>
                  <div className="order-details">
                    <p><Calendar size={16} /> {formatDate(order.createdAt)}</p>
                    <p>{order.items?.length || 0} item(s)</p>
                  </div>
                  <div className="order-actions">
                    <button 
                      className="btn btn-primary btn-sm"
                      onClick={() => navigate(`/order/${order.id}`)}
                    >
                      Ver Detalhes
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'quotes' && (
        <div className="quotes-section">
          {quotes.length === 0 ? (
            <div className="empty-state">
              <FileText size={64} color="#ccc" />
              <h3>Nenhum orçamento encontrado</h3>
              <p>Você ainda não solicitou nenhum orçamento. Solicite agora!</p>
            </div>
          ) : (
            <div className="quotes-grid">
              {quotes.map(quote => (
                <div key={quote.id} className="quote-card">
                  <div className="quote-header">
                    <h3>Orçamento #{quote.id}</h3>
                    <span 
                      className="status-badge"
                      style={{ backgroundColor: getQuoteStatusColor(quote.status) }}
                    >
                      {getQuoteStatusText(quote.status)}
                    </span>
                  </div>
                  <div className="quote-details">
                    <p><Calendar size={16} /> {formatDate(quote.createdAt)}</p>
                    <p><strong>Tipo:</strong> {quote.type === 'simple' ? 'Simples' : 'Avançado'}</p>
                    <div className="customer-info">
                      <p><User size={16} /> {quote.customerInfo?.name}</p>
                      <p><Envelope size={16} /> {quote.customerInfo?.email}</p>
                      <p><Phone size={16} /> {quote.customerInfo?.phone}</p>
                    </div>
                    {quote.type === 'simple' && quote.message && (
                      <div className="quote-message">
                        <strong>Mensagem:</strong>
                        <p>{quote.message.length > 100 ? quote.message.substring(0, 100) + '...' : quote.message}</p>
                      </div>
                    )}
                    {quote.type === 'advanced' && quote.products && Array.isArray(quote.products) && (
                      <div className="quote-products">
                        <strong>Produtos:</strong>
                        <p>{quote.products.reduce((sum, product) => sum + product.quantity, 0)} item(s) selecionado(s)</p>
                      </div>
                    )}
                  </div>
                  <div className="quote-actions">
                    <button 
                      className="btn btn-primary btn-sm"
                      onClick={() => navigate(`/quote/${quote.id}`)}
                    >
                      Ver Detalhes
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Orders;
